/**
 * wizardProgress.js — RC 1.49.x Slice 4 (2026-05-07).
 *
 * Working-state UX consistency for the three remaining long-running
 * candidate actions in `JobApplicationWizard.jsx`:
 *   • Generate Tailored CV  (kind: 'cv')
 *   • Generate Cover Letter (kind: 'letter')
 *   • Create Application Pack (kind: 'pack')
 *
 * Mirrors the Slice 1B / Slice 2B working-state pattern — calm
 * rotating reassurance copy, aria-live polite, clamped at the last
 * message (no looping back), with humanised generic 5xx errors.
 *
 * Why a shared helper?
 * ────────────────────
 * AddJobDialog (Slice 1B) and JobDetailPage Analyse Fit (Slice 2B)
 * each had their own bespoke copy + ticker. As we extend the
 * pattern to three more flows, copy and clamp logic should live
 * exactly once so future operator-locked tweaks (timing, copy,
 * accessibility) don't have to be replicated across 3+ files.
 *
 * Hard rails (operator-locked, 2026-05-07):
 *   • No backend changes / no new endpoint / no async architecture.
 *   • No queues, no SSE, no WebSocket.
 *   • Helper is dependency-free (only React).
 *   • Humaniser opts out of `USAGE_EXHAUSTED` /
 *     `FREE_DAILY_ANALYZE_LIMIT_REACHED` so existing entitlement
 *     modals still drive the UX for those branches.
 */

import { useEffect, useRef, useState } from 'react';

/** Tick interval for rotating the progress message. Matches the
 *  Slice 1B AddJobDialog cadence so the experience feels uniform
 *  across the app. */
export const PROGRESS_TICK_MS = 3500;

/** Per-kind message arrays. Operator-locked phrasing: any change
 *  here MUST be paired with the corresponding regex assertion in
 *  the contract test. */
export const PROGRESS_MESSAGES_CV = [
  'Preparing your tailored CV…',
  'Analysing the job requirements…',
  'Aligning your experience…',
  'Finalising your document…',
  'Almost done…',
];
export const PROGRESS_MESSAGES_LETTER = [
  'Drafting your letter…',
  'Matching tone and context…',
  'Preparing final wording…',
  'Almost done…',
];
export const PROGRESS_MESSAGES_PACK = [
  'Building your application pack…',
  'Assembling documents…',
  'Preparing export assets…',
  'Finalising your pack…',
];

/** Internal lookup. Unknown kinds fall back to the CV array — that
 *  prevents a typo at a call site from rendering a blank string. */
const KIND_TO_MESSAGES = {
  cv: PROGRESS_MESSAGES_CV,
  letter: PROGRESS_MESSAGES_LETTER,
  pack: PROGRESS_MESSAGES_PACK,
};

/**
 * Rotating reassurance message hook.
 *
 * @param {boolean} active  — typically the matching `generating` /
 *                            `letterGenerating` / `creating` flag.
 * @param {'cv' | 'letter' | 'pack'} kind
 * @returns {string | null} — the current message while `active`,
 *                            or `null` when inactive (so callers
 *                            can short-circuit the strip render).
 *
 * Behaviour:
 *   • Resets the tick to 0 every time `active` flips from false →
 *     true (subsequent runs always start at the first message).
 *   • Clamps at the last entry (no infinite loop — rolling back to
 *     "step one" would feel like the action restarted).
 *   • Cleans up the interval on unmount and on `active` false.
 */
export function useRotatingProgress(active, kind) {
  const [tick, setTick] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTick(0);
      return undefined;
    }
    setTick(0);
    timerRef.current = setInterval(() => {
      setTick((t) => t + 1);
    }, PROGRESS_TICK_MS);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active]);

  if (!active) return null;
  const arr = KIND_TO_MESSAGES[kind] || PROGRESS_MESSAGES_CV;
  return arr[Math.min(tick, arr.length - 1)];
}

/** Operator-locked humanisation copy for generic 5xx / empty / raw
 *  "internal server error" responses. Targeted backend `detail.message`
 *  strings still pass through verbatim. */
const HUMANIZE_KIND_BLURB = {
  cv: "We couldn't generate the tailored CV from this job yet. Please try again, or edit the job details.",
  letter: "The cover letter couldn't be completed because the job description appears incomplete.",
  pack: "Your application pack could not be assembled yet. Please retry in a moment.",
};

/**
 * Humanise an error from any of the three wizard flows.
 *
 * Returns:
 *   • `null` when the caller should defer to its own existing
 *     branch (USAGE_EXHAUSTED → entitlement modal,
 *     FREE_DAILY_ANALYZE_LIMIT_REACHED → free-limit modal).
 *   • A user-shaped backend `detail.message` when present on a 4xx.
 *   • The kind-specific blurb for generic 5xx / empty / raw
 *     "internal server error" shapes.
 */
export function humaniseWizardError(err, kind) {
  const status = err?.status || err?.response?.status;
  const code = err?.data?.detail?.code;
  const detailMsg = err?.data?.detail?.message;
  const raw = (err?.message || '').trim();

  // Opt out — the wizard already routes these through dedicated
  // entitlement / paywall modals.
  if (code === 'USAGE_EXHAUSTED') return null;
  if (code === 'FREE_DAILY_ANALYZE_LIMIT_REACHED') return null;

  // 4xx with a real, user-shaped detail message: pass through.
  if (status >= 400 && status < 500 && detailMsg) return detailMsg;

  // Generic 5xx / empty / "internal server error" / "Request failed".
  const looksGeneric = !raw
    || /^internal server error\.?$/i.test(raw)
    || /^server error\.?$/i.test(raw)
    || /^request failed( with status code \d+)?\.?$/i.test(raw);
  if (looksGeneric || (status >= 500 && status < 600)) {
    return HUMANIZE_KIND_BLURB[kind] || raw || 'Action failed.';
  }
  return raw || HUMANIZE_KIND_BLURB[kind] || 'Action failed.';
}
